import { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppNavbar } from "../components/layout/AppNavbar";
import { AppSidebar } from "../components/layout/AppSidebar";
import { useAuth } from "@/context/authContext";
import { useMyAccount } from "@/features/account/hooks/useMyAccount";
import { PageTransition } from "@/components/ui/page-transition";

const AdminWrapper = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { data: account } = useMyAccount();

    // Auto-close sidebar on mobile when route changes
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // A deactivated user is signed out as soon as the app notices (his data is blocked by RLS anyway)
    const deactivated = account?.is_active === false;
    useEffect(() => {
        if (!deactivated) return;
        void logout().finally(() => navigate("/login?inactive=1", { replace: true }));
    }, [deactivated, logout, navigate]);

    if (!user) {
        return <Navigate to="/login" replace />
    }

    // Temporary password: nothing else until it is changed
    if (account?.must_change_password && location.pathname !== "/cuenta") {
        return <Navigate to="/cuenta?cambiar=1" replace />
    }

    return (
        <div className="flex h-screen w-screen flex-col bg-gradient-to-br from-background via-background to-muted/20">
            {/* Navbar with subtle shadow */}
            <AppNavbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
            
            {/* Main Layout */}
            <div className="flex flex-1 overflow-hidden">
                {/* Desktop Sidebar */}
                <div className="hidden lg:flex">
                    <AppSidebar />
                </div>

                {/* Mobile Sidebar with smoother transition */}
                {sidebarOpen && (
                    <div 
                        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden transition-all duration-300"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <div 
                            className="fixed inset-y-0 left-0 w-72 bg-card shadow-2xl transform transition-transform duration-300 ease-out"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <AppSidebar />
                        </div>
                    </div>
                )}

                {/* Main Content with padding and smooth scroll */}
                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                    <PageTransition>
                        <div className="mx-auto max-w-7xl">
                            <Outlet />
                        </div>
                    </PageTransition>
                </main>
            </div>
        </div>
    );
}

export default AdminWrapper;
